import { searchDimmingOpacity } from '../calendarSearchModel'
import { useCalendarSearchHighlight } from '../CalendarSearchHighlightContext'
import { useState, useRef, useCallback } from "react"
import { useDraggable } from "@dnd-kit/core"
import { Popover, Tooltip } from "antd"
import dayjs from "dayjs"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTriangleExclamation, faSnowflake } from "@fortawesome/pro-solid-svg-icons"
import { DateRangeViewEvent } from "../scheduleApi"
import { EventDisplayInfo, isCrewEditable, isEventDragDisabled } from "../scheduleModel"
import { RESCHEDULED_EVENT_STRIPE_PATTERN, HOVER_POPOVER_OVERLAY_STYLE } from "../scheduleEventStyles"
import EventStatusIcon from "../EventStatusIcon"
import { getEventCardColors } from "../scheduleEventColorsModel"
import AvatarGroup from "../DayView/AvatarGroup"
import EventSelectCrew from "../EventSelectCrew"
import EventPopoverContent from "../EventPopoverContent"

export type Props = {
  event: DateRangeViewEvent
  displayInfo?: EventDisplayInfo
  onEventClick: (event: DateRangeViewEvent) => void
}

const MonthViewEvent = ({ event, displayInfo, onEventClick }: Props) => {
  const { searchTerm: highlightTerm } = useCalendarSearchHighlight()
  const { attributes, listeners, setNodeRef: setDraggableRef, isDragging, transform } = useDraggable({
    id: `event-${event.id}`,
    data: { event },
    disabled: isEventDragDisabled(event, displayInfo),
  })

  const [elementWidth, setElementWidth] = useState<number>(0)
  const showAvatars = elementWidth >= 120
  const [isHovering, setIsHovering] = useState(false)

  const resizeObserverRef = useRef<ResizeObserver | null>(null)

  const setNodeRef = useCallback((node: HTMLDivElement | null) => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect()
    }

    if (node) {
      resizeObserverRef.current = new ResizeObserver((entries) => {
        const entry = entries[0]
        if (entry) {
          setElementWidth(entry.contentRect.width)
        }
      })
      resizeObserverRef.current.observe(node)
      setElementWidth(node.offsetWidth)
    }

    setDraggableRef(node)
  }, [setDraggableRef])

  const startTime = dayjs(event.start)

  const formatTime = () => {
    if (event.allDay) {
      return 'All Day'
    }
    return startTime.format('h:mmA')
  }

  const { backgroundColor, palette } = getEventCardColors(event.crewForemanColor)

  const crewEditable = isCrewEditable(displayInfo)

  const isRescheduled = event.rescheduledToEventId != null

  const style: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: '4px 8px',
    marginBottom: 2,
    borderRadius: 4,
    backgroundColor,
    color: palette.primaryText,
    cursor: isDragging ? 'grabbing' : 'pointer',
    overflow: 'hidden',
    position: 'relative',
    zIndex: isDragging ? 100 : undefined,
    transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
    backgroundImage: isRescheduled ? RESCHEDULED_EVENT_STRIPE_PATTERN : undefined,
  }

  return (
    <Popover
      content={
        <EventPopoverContent
          event={event}
          workareaStatuses={displayInfo?.workareaStatuses}
          timesheets={displayInfo?.timesheets}
          warning={displayInfo?.warning ?? undefined}
          rescheduledToDate={displayInfo?.rescheduledToDate}
        />
      }
      open={isHovering && !isDragging}
      placement="right"
      overlayStyle={HOVER_POPOVER_OVERLAY_STYLE}
    >
      <div
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        onClick={(e) => {
          e.stopPropagation()
          onEventClick(event)
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        style={{ ...style, opacity: searchDimmingOpacity(event, highlightTerm) }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {displayInfo && showAvatars && (
            <EventStatusIcon status={displayInfo.status} />
          )}
          {event.frozen && (
            <Tooltip title="Frozen">
              <FontAwesomeIcon icon={faSnowflake} style={{ color: palette.secondaryText, fontSize: 12, flexShrink: 0 }} />
            </Tooltip>
          )}
          <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0, textDecoration: displayInfo?.areAllWorkAreasComplete ? 'line-through' : undefined }}>
            {event.customer?.displayName}
          </span>
          {showAvatars && (
            <div style={{ flexShrink: 0 }}>
              {crewEditable
                ? <EventSelectCrew event={event} variant="avatar" color={palette.primaryText} />
                : <AvatarGroup crewForemanEmail={event.crewForemanEmail} crewMemberIds={event.crewMemberIds} initialsColor={palette.primaryText} />
              }
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden' }}>
          {displayInfo?.warning && (
            <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: '#fa8c16', fontSize: 14, flexShrink: 0 }} />
          )}
          <span style={{ color: palette.secondaryText, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: displayInfo?.areAllWorkAreasComplete ? 'line-through' : undefined }}>{event.jobName}</span>
        </div>
        <span style={{ color: palette.secondaryText, fontSize: 12, whiteSpace: 'nowrap' }}>{formatTime()}</span>
      </div>
    </Popover>
  )
}

export default MonthViewEvent
